import type { ReactNode } from 'react';
import type { ApiFailure } from '../api/client.js';

export function Panel({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="panel">
      {title !== undefined && <h2>{title}</h2>}
      {children}
    </section>
  );
}

export function Row({ children }: { children: ReactNode }) {
  return <div className="row">{children}</div>;
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <label className="field">
      {label}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function Button({
  children,
  onClick,
  disabled,
  variant,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'secondary' | 'danger';
}) {
  return (
    <button
      type="button"
      className={variant === undefined ? 'action' : `action ${variant}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function KeyValue({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="kv">
      {rows.map(([key, value]) => (
        <div key={key} style={{ display: 'contents' }}>
          <dt>{key}</dt>
          <dd>{value ?? <span className="muted">null</span>}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Badge({ children, tone }: { children: ReactNode; tone?: 'ok' | 'warn' | 'bad' }) {
  return <span className={tone === undefined ? 'badge' : `badge ${tone}`}>{children}</span>;
}

/** Renders `true`/`false`/`null` without collapsing false into "nothing". */
export function Bool({ value }: { value: boolean | null }) {
  if (value === null) return <span className="muted">null</span>;
  return (
    <Badge tone={value ? 'ok' : undefined}>{value ? 'yes' : 'no'}</Badge>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Loading() {
  return <p className="empty">loading…</p>;
}

/** The raw body, always available. This is what makes the app a test tool. */
export function Raw({ label = 'raw response', value }: { label?: string; value: unknown }) {
  return (
    <details className="raw">
      <summary>{label}</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

/**
 * Every failure, rendered the same way: the backend's own code and message, the
 * status line, and the untouched body. Unknown codes still show their message —
 * the union in src/errors/codes.ts will grow, and "something went wrong" would
 * hide exactly the information this app exists to surface.
 */
export function Failure({ error, hint }: { error: ApiFailure; hint?: ReactNode }) {
  return (
    <div className="failure">
      <div>
        <span className="code">{error.code}</span>{' '}
        <span className="muted">HTTP {error.status === 0 ? '—' : error.status}</span>
      </div>
      <div>{error.message}</div>
      {error.retryAfter !== null && (
        <div className="muted">retry_after: {error.retryAfter}s</div>
      )}
      {hint !== undefined && <div className="muted" style={{ marginTop: 6 }}>{hint}</div>}
      {error.body !== null && <Raw label="error body" value={error.body} />}
    </div>
  );
}

/** A read's three states in one place, so no screen forgets one. */
export function Async<T>({
  state,
  children,
  empty,
}: {
  state: { data: T | null; error: ApiFailure | null; loading: boolean };
  children: (data: T) => ReactNode;
  empty?: ReactNode;
}) {
  if (state.error !== null) return <Failure error={state.error} />;
  if (state.data !== null) return <>{children(state.data)}</>;
  if (state.loading) return <Loading />;
  return <Empty>{empty ?? 'nothing here'}</Empty>;
}

export function Note({ children }: { children: ReactNode }) {
  return <div className="note">{children}</div>;
}

export function Screen({
  title,
  lede,
  children,
}: {
  title: string;
  lede?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      <h1>{title}</h1>
      {lede !== undefined && <p className="lede">{lede}</p>}
      {children}
    </>
  );
}
